import { motion } from "framer-motion";
import { useEffect } from "react";
import heroVideo from "../assests/health.mp4"; // Replace with your video path

export default function HealthcareAI() {
  
  return (
    <div className="bg-gray-50 text-gray-900 font-orbitron">

      {/* Hero Section */}
      <section className="relative w-full h-[70vh] flex items-center justify-center text-center overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/70 via-purple-500/50 to-teal-500/70"></div>

        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 px-6 max-w-3xl"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white drop-shadow-lg">
            Healthcare AI & Data Solutions
          </h1>
          <p className="text-lg md:text-xl text-gray-100 leading-relaxed">
            Enable smarter healthcare solutions with high-quality datasets, accurate annotations, and AI-driven insights.
          </p>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-semibold mb-6 text-indigo-700"
        >
          Why Healthcare AI Matters
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-lg text-gray-700 leading-relaxed"
        >
          AI and data-driven solutions are revolutionizing healthcare. Accurate datasets are key to building reliable AI models for diagnostics, patient care, and medical research.
        </motion.p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-teal-50 via-white to-indigo-50">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-semibold text-center mb-12 text-indigo-700"
        >
          Our Healthcare Services
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            { title: "🩺 Medical Imaging Annotation", desc: "Accurate labeling for X-rays, MRIs, CT scans, and ultrasound datasets.", color: "from-purple-400 to-indigo-500" },
            { title: "💊 Clinical Text & Speech QA", desc: "Transcribed and validated clinical notes, prescriptions, and consultations.", color: "from-teal-400 to-blue-500" },
            { title: "🔬 Data Labeling & Validation", desc: "Expert-reviewed annotations for images, videos, and structured data.", color: "from-pink-400 to-purple-500" },
            { title: "📊 Predictive Analytics Data", desc: "High-quality AI-ready data for diagnostics and patient outcomes.", color: "from-green-400 to-teal-500" },
            { title: "🌍 Multilingual & Diverse Datasets", desc: "Ensure global applicability and reduce bias in AI models.", color: "from-orange-400 to-red-500" },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className={`bg-gradient-to-br ${item.color} p-6 rounded-2xl shadow-lg text-white hover:shadow-2xl transition`}
            >
              <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
              <p className="leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-semibold text-center mb-12 text-indigo-700"
        >
          Our Workflow
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Analyze healthcare data requirements",
            "Curate medical images, text, and audio",
            "Annotate with domain experts",
            "QA & compliance review",
            "Deliver ready-to-use AI datasets",
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-md hover:shadow-lg"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-800 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-center bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-5xl font-bold mb-6"
        >
          Empower Healthcare AI with Vibrant Data
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-lg max-w-xl mx-auto mb-6"
        >
          Connect with us to access high-quality medical datasets and expert annotation services to build smarter, safer healthcare solutions.
        </motion.p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-purple-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          Get Started Today
        </motion.button>
      </section>
    </div>
  );
}
